import { useEffect, useRef, type KeyboardEvent } from "react";

import { formatMetres } from "@/lib/format";

import { Button } from "./Button";
import styles from "./LengthDoubt.module.css";

interface LengthDoubtProps {
  /** The id of the question — for the field's `aria-describedby`. */
  questionId: string;
  /** What was typed, in mm. */
  mm: number;
  /** What is offered instead (`wallLengthDoubt`). */
  suggestionMm: number;
  /** The answer: the figure offered, or the one typed. */
  onAnswer: (mm: number) => void;
  /** Escape: no answer, back to the field. */
  onDismiss: () => void;
  /**
   * Whether the question takes the focus when it appears (default). Not where
   * it is asked because the person left the field for somewhere else — their
   * next keystrokes belong there (Task 15c).
   */
  autoFocus?: boolean;
}

/**
 * „60,00 m — so lang? Meintest du 6,00 m?“ (UX-Gutachten 23.10.2026, Nr. 7)
 * — the one question a wall length longer than any facade asks, on the
 * board's „Maß“ and in the outline editor alike, so both say it in the same
 * words and take the same answers.
 *
 * A group named by the question; the focus goes to the figure offered, so a
 * keyboard answers at once and a screen reader hears the question as the
 * group's name. Escape leaves it without an answer. „eintragen“, not
 * „übernehmen“: in the outline editor „Übernehmen“ writes the Stand, and the
 * answer here only fills the field. „Bestätigung nötig“ (PRD §18.2) is its
 * status — the rule at its edge, never the text.
 */
export function LengthDoubt({ questionId, mm, suggestionMm, onAnswer, onDismiss, autoFocus = true }: LengthDoubtProps) {
  const ref = useRef<HTMLDivElement>(null);
  const focusOnMount = useRef(autoFocus);
  useEffect(() => {
    if (focusOnMount.current) ref.current?.querySelector<HTMLButtonElement>("button")?.focus();
  }, []);

  function keyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key !== "Escape") return;
    event.preventDefault();
    event.stopPropagation();
    onDismiss();
  }

  return (
    <div
      ref={ref}
      role="group"
      aria-labelledby={questionId}
      className={styles.doubt}
      data-role="length-doubt"
      onKeyDown={keyDown}
    >
      <p id={questionId} className={styles.question}>
        <span className="num">{formatMetres(mm)}</span> — so lang? Meintest du{" "}
        <span className="num">{formatMetres(suggestionMm)}</span>?
      </p>
      <div className={styles.answers}>
        <Button onClick={() => onAnswer(suggestionMm)}>
          <span className="num">{formatMetres(suggestionMm)}</span> eintragen
        </Button>
        {/* Outlined too, not the quiet variant: a long wall is a real
            answer, and the quiet one underlined word and figure apart. */}
        <Button onClick={() => onAnswer(mm)}>
          Ja, <span className="num">{formatMetres(mm)}</span>
        </Button>
      </div>
    </div>
  );
}
